import HostPageEventTracker from './host-page-event.tracker'
import {
  HostPageEventData,
  HostPageEventName,
  HostPageEventSource,
  HostPageMParticleEventInfo,
  HostPageMParticleEventType,
  QueuedHostPageEvent,
} from './host-page-event.types'
import { HostPageEventTrackerProps } from './host-page-event.tracker'

export const HOST_PAGE_EVENT_FLUSH_MODES = {
  IMMEDIATE: 'immediate',
  MANUAL: 'manual',
} as const

type HostPageEventTrackerKind = 'event' | 'funnel' | 'ui-element'

export type HostPageEventFlushMode =
  (typeof HOST_PAGE_EVENT_FLUSH_MODES)[keyof typeof HOST_PAGE_EVENT_FLUSH_MODES]

export interface TrackHostPageEventByDefinitionProps<TData extends HostPageEventData> {
  campaignId: string
  componentId?: string
  data: TData
}

interface HostPageEventDefinitionBase<TData extends HostPageEventData> {
  buildMParticleEventInfo?: (
    event: TrackHostPageEventByDefinitionProps<TData>
  ) => HostPageMParticleEventInfo | undefined
  flushMode?: HostPageEventFlushMode
  hostPageEventName: HostPageEventName
  mparticleEventName: string
  mparticleEventType?: HostPageMParticleEventType
  source: HostPageEventSource
}

export interface HostPageEventDefinition<TData extends HostPageEventData>
  extends HostPageEventDefinitionBase<TData> {
  tracker: HostPageEventTrackerKind
}

type HostPageEventDefinitionWithoutTracker<TData extends HostPageEventData> = Omit<
  HostPageEventDefinition<TData>,
  'tracker'
>

export const defineHostPageEvent = <TData extends HostPageEventData>(
  definition: HostPageEventDefinitionWithoutTracker<TData>
): HostPageEventDefinition<TData> => {
  return {
    ...definition,
    tracker: 'event',
  }
}

export const defineHostPageFunnelEvent = <TData extends HostPageEventData>(
  definition: HostPageEventDefinitionWithoutTracker<TData>
): HostPageEventDefinition<TData> => {
  return {
    ...definition,
    tracker: 'funnel',
  }
}

export const defineHostPageUIElementEvent = <TData extends HostPageEventData>(
  definition: HostPageEventDefinitionWithoutTracker<TData>
): HostPageEventDefinition<TData> => {
  return {
    ...definition,
    tracker: 'ui-element',
  }
}

export default class HostPageEventInstrumentation {
  private tracker: HostPageEventTracker

  constructor(tracker: HostPageEventTracker) {
    this.tracker = tracker
  }

  public track = <TData extends HostPageEventData>(
    definition: HostPageEventDefinition<TData>,
    event: TrackHostPageEventByDefinitionProps<TData>
  ): QueuedHostPageEvent => {
    const flush = definition.flushMode === HOST_PAGE_EVENT_FLUSH_MODES.IMMEDIATE
    const trackEvent = this.getTracker(definition.tracker)

    return trackEvent(
      {
        campaignId: event.campaignId,
        componentId: event.componentId,
        data: event.data,
        mparticleEventInfo: definition.buildMParticleEventInfo?.(event),
        mparticleEventName: definition.mparticleEventName,
        mparticleEventType: definition.mparticleEventType,
        name: definition.hostPageEventName,
        source: definition.source,
      },
      { flush }
    )
  }

  private getTracker = (tracker: HostPageEventTrackerKind) => {
    if (tracker === 'ui-element') {
      return this.tracker.trackUIElement
    }

    if (tracker === 'funnel') {
      return this.tracker.trackFunnelEvent
    }

    return this.tracker.trackEvent
  }
}

export const createHostPageEventInstrumentation = (
  props: HostPageEventTrackerProps
): HostPageEventInstrumentation => {
  return new HostPageEventInstrumentation(new HostPageEventTracker(props))
}
